/**
 * Data Transformation Service
 * 
 * This service transforms data from the FastAPI backend format
 * to the format expected by the React frontend components
 */

/**
 * Transform farm data from API format to frontend format
 * @param {Object} apiData - Raw API data from the backend
 * @returns {Object} - Transformed data ready for frontend components
 */
export const transformFarmData = (apiData) => {
  if (!apiData) return null;
  
  console.log(`Transforming data for farm ID: ${apiData.id}`);
  
  // Store farm ID for validation at each step
  const farmId = apiData.id;
  
  // Explicitly pass farm ID to each transform function to maintain data consistency
  const transformedData = {
    // Essential farm identity data
    id: farmId, // Preserve the farm ID
    name: apiData.name || `Farm ${farmId}`,
    location: apiData.location || { lat: 0, lng: 0 },
    
    // Farm specifications
    farmSize: apiData.farm_size_ha || apiData.area_hectares || 0,
    cropType: apiData.crop_type || 'Unknown',
    plantingDate: apiData.planting_date || null,
    harvestDate: apiData.expected_harvest_date || null,
    boundary: apiData.boundary || null,
    
    // Farmer information
    farmer: {
      name: apiData.farmer_name || 'Unknown Farmer',
      contact: apiData.farmer_contact || 'No contact information',
      history: apiData.farmer_history || []
    },
    
    // Analysis data - pass farmId to transform functions
    ndvi: transformNDVIData(apiData.ndvi, farmId),
    yieldForecast: transformYieldForecast(apiData.yieldForecast, farmId),
    riskAssessment: transformRiskAssessment(apiData.riskAssessment, farmId),
    
    // Financial data
    loanRequest: {
      amount: apiData.loan_amount || 0,
      purpose: apiData.loan_purpose || 'Farm development',
      term: apiData.loan_term_months || 12
    },
    previousLoans: apiData.previous_loans || [],
    
    // Additional data
    soilMoisture: apiData.soil_moisture || { value: 0, unit: '%', trend: 'neutral' },
    weatherData: apiData.weather_data || { 
      temperature: 0,
      rainfall: 0,
      forecast: []
    },
  };
  
  console.log(`Transformed data for farm ${farmId}:`, {
    id: transformedData.id,
    name: transformedData.name,
    ndvi_value: transformedData.ndvi?.current,
    yield_value: transformedData.yieldForecast?.predicted,
    risk_overall: transformedData.riskAssessment?.overall
  });
  
  return transformedData;
};

/**
 * Transform NDVI data from API format to frontend format
 * @param {Object} ndviData - Raw NDVI data from API
 * @param {String} farmId - Farm ID to validate data belongs to correct farm
 * @returns {Object} - Transformed NDVI data
 */
export const transformNDVIData = (ndviData, farmId) => {
  if (!ndviData) {
    console.log(`No NDVI data available for farm ${farmId}, using defaults`);
    return {
      current: 0,
      average: 0,
      historical: [],
      trend: 'neutral',
      farm_id: farmId // Add farm ID to help with debugging
    };
  }
  
  // Validate this data belongs to the correct farm if farm_id is present
  if (ndviData.farm_id && ndviData.farm_id !== farmId) {
    console.warn(`NDVI data mismatch! Expected farm ${farmId}, got ${ndviData.farm_id}`);
  }
  
  // Get current value from various possible API response formats
  const currentValue = ndviData.current_value !== undefined ? ndviData.current_value : 
                      (ndviData.value !== undefined ? ndviData.value : 0);
  
  return {
    current: currentValue,
    average: ndviData.average_value || 0,
    historical: ndviData.historical_values || [],
    trend: ndviData.trend || 'neutral',
    farm_id: farmId, // Always include farmId for data validation
    timestamp: ndviData.timestamp || new Date().toISOString()
  };
};

/**
 * Transform yield forecast data from API format to frontend format
 * @param {Object} forecastData - Raw yield forecast data from API
 * @param {String} farmId - Farm ID to validate data belongs to correct farm
 * @returns {Object} - Transformed yield forecast data
 */
export const transformYieldForecast = (forecastData, farmId) => {
  if (!forecastData) {
    console.log(`No yield forecast data available for farm ${farmId}, using defaults`);
    return {
      predicted: 0,
      unit: 'tonnes/ha',
      confidence: {
        low: 0,
        high: 0
      },
      historical: [],
      trend: 'neutral',
      farm_id: farmId // Add farm ID to help with debugging
    };
  }
  
  // Validate this data belongs to the correct farm if farm_id is present
  if (forecastData.farm_id && forecastData.farm_id !== farmId) {
    console.warn(`Yield forecast data mismatch! Expected farm ${farmId}, got ${forecastData.farm_id}`);
  }
  
  // Get predicted yield from various possible API response formats
  const predictedYield = forecastData.predicted_yield !== undefined ? forecastData.predicted_yield :
                        (forecastData.yield_value !== undefined ? forecastData.yield_value :
                        (forecastData.predicted !== undefined ? forecastData.predicted : 0));
  
  return {
    predicted: predictedYield,
    unit: forecastData.yield_unit || 'tonnes/ha',
    confidence: {
      low: forecastData.confidence_interval?.low || 0,
      high: forecastData.confidence_interval?.high || 0
    },
    historical: forecastData.historical_yields || [],
    trend: forecastData.trend || 'neutral',
    farm_id: farmId, // Always include farmId for data validation
    timestamp: forecastData.timestamp || new Date().toISOString(),
    metadata: forecastData.metadata || {}
  };
};

/**
 * Transform risk assessment data from API format to frontend format
 * @param {Object} riskData - Raw risk assessment data from API
 * @param {String} farmId - Farm ID to validate data belongs to correct farm
 * @returns {Object} - Transformed risk assessment data
 */
export const transformRiskAssessment = (riskData, farmId) => {
  if (!riskData) {
    console.log(`No risk assessment data available for farm ${farmId}`);
    return null; // Return null instead of default values
  }
  
  // Log the structure of the risk assessment data
  console.log(`Risk assessment data structure for farm ${farmId}:`, Object.keys(riskData));
  
  // Validate this data belongs to the correct farm if farm_id is present
  if (riskData.farm_id && riskData.farm_id !== farmId) {
    console.warn(`Risk assessment data mismatch! Expected farm ${farmId}, got ${riskData.farm_id}`);
    // Create a new copy with the correct farm ID
    return {
      ...riskData,
      farm_id: farmId
    };
  }
  
  // Important: Return the original data structure exactly as provided by the API
  // Only ensure farm_id is present
  return riskData.farm_id ? riskData : { ...riskData, farm_id: farmId };
};

export default {
  transformFarmData,
  transformNDVIData,
  transformYieldForecast,
  transformRiskAssessment
};
